import { Box, Button, Flex, Image, Input, Text } from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { RichTextField } from '../../../../components/RichTextField'
import { FormSelect } from '../../../../components/Select'
import { IProfessionalFields } from '../../../../services/Event/EventService'
import { SelectTeacher } from '../../../../services/Teacher/types'

export const baseProfessional = (): IProfessionalFields => ({
  _id: crypto.randomUUID(),
  teacher_id: null,
  title: '',
  name: '',
  bio: '',
  thumb: null,
  thumbPreview: null
})

interface IProps {
  professionals: IProfessionalFields[]
  onChange: (next: IProfessionalFields[]) => void
  teachers: SelectTeacher[]
  disabled?: boolean
  /** Field errors keyed by dotted path (e.g. `editions.0.professionals.0.title`). */
  fieldErrors?: Record<string, string[]>
  /** Prefix for the error keys, e.g. `editions.0.professionals.`. */
  errorPrefix?: string
}

/** Add/edit/remove professionals (external or from the teachers list). */
export function ProfessionalsEditor({
  professionals,
  onChange,
  teachers,
  disabled,
  fieldErrors,
  errorPrefix = ''
}: IProps) {
  const errorFor = (index: number, field: 'title' | 'name') => fieldErrors?.[`${errorPrefix}${index}.${field}`]?.[0]
  const addExternal = () => onChange([...professionals, baseProfessional()])

  const addTeacher = (teacherId: number) => {
    const teacher = teachers.find((t) => t.id === teacherId)
    if (!teacher) return
    onChange([
      ...professionals,
      {
        _id: crypto.randomUUID(),
        teacher_id: teacher.id,
        title: teacher.staff_body_text ?? '',
        name: teacher.name,
        bio: teacher.iam_text || '',
        thumb: null,
        thumbPreview: null
      }
    ])
  }

  const update = (id: string, field: keyof IProfessionalFields, value: string) =>
    onChange(professionals.map((p) => (p._id === id ? { ...p, [field]: value } : p)))

  const remove = (id: string) => onChange(professionals.filter((p) => p._id !== id))

  return (
    <>
      <Flex gap={3} alignItems="center">
        <Button size="sm" variant="outline" colorScheme="gray" onClick={addExternal} disabled={disabled}>
          + Adicionar Profissional Externo
        </Button>
        <FormSelect
          size="sm"
          isDisabled={disabled}
          w="200px"
          value=""
          onChange={(value) => addTeacher(Number(value))}
          options={teachers
            .filter((t) => !professionals.some((p) => p.teacher_id === t.id))
            .map((t) => ({ value: t.id, label: t.name }))}
          placeholder="Selecionar Professor"
        />
      </Flex>

      {professionals.map((prof, index) => {
        const isTeacher = !!prof.teacher_id
        const teacherBanner = teachers.find((t) => t.id === prof.teacher_id)?.banner_picture
        return (
          <Box key={prof._id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} mt={3}>
            <Flex justifyContent="space-between" alignItems="center" mb={3}>
              <Text fontWeight="bold" fontSize="sm">
                {isTeacher ? '👨‍🏫 Professor' : '👤 Profissional Externo'}
              </Text>
              <Button size="xs" colorScheme="red" variant="ghost" onClick={() => remove(prof._id)} disabled={disabled}>
                Remover
              </Button>
            </Flex>

            <Flex direction="column" gap={3}>
              <Flex gap={3}>
                <FormField label="Título" error={errorFor(index, 'title')} isRequired flex="1">
                  <Input
                    size="sm"
                    value={prof.title}
                    onChange={(e) => update(prof._id, 'title', e.target.value)}
                    disabled={disabled || isTeacher}
                    bg={isTeacher ? 'gray.50' : undefined}
                  />
                </FormField>
                <FormField label="Nome" error={errorFor(index, 'name')} isRequired flex="1">
                  <Input
                    size="sm"
                    value={prof.name}
                    onChange={(e) => update(prof._id, 'name', e.target.value)}
                    disabled={disabled || isTeacher}
                    bg={isTeacher ? 'gray.50' : undefined}
                  />
                </FormField>
              </Flex>

              {!isTeacher && (
                <ImageCropInput
                  label="Foto"
                  value={prof.thumb ?? prof.thumbPreview ?? null}
                  onChange={(file) =>
                    onChange(
                      professionals.map((p) => (p._id === prof._id ? { ...p, thumb: file, thumbPreview: null } : p))
                    )
                  }
                  maxSizeMB={5}
                  previewHeight="150px"
                  isDisabled={disabled}
                />
              )}

              {isTeacher && teacherBanner && (
                <Box>
                  <FormField label="Foto">
                    <Image src={teacherBanner} alt={prof.name} boxSize="300px" objectFit="contain" borderRadius="md" />
                  </FormField>
                </Box>
              )}

              <RichTextField
                label="Mini Bio"
                value={prof.bio}
                onChange={(bio) => update(prof._id, 'bio', bio)}
                placeholder="Mini bio do profissional..."
                isReadOnly={isTeacher}
              />
            </Flex>
          </Box>
        )
      })}
    </>
  )
}
